import { Head } from "@inertiajs/react"
import { useState, useEffect } from "react"
import { router } from "@inertiajs/react"
import { FileText, Star, ArrowUp, ArrowDown, ArrowUpDown, Plus, Loader2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu"

type SortField = "name" | "clientModified" | "serverModified"
type SortDirection = "asc" | "desc"

const SORT_LABELS: Record<SortField, string> = {
  name: "Name",
  clientModified: "Created",
  serverModified: "Updated",
}

interface FileEntry {
  name: string
  path: string
  type: "file" | "folder"
  clientModified?: string
  serverModified?: string
  tags?: Record<string, string>
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

export default function RecentFiles() {
  const [recentFiles, setRecentFiles] = useState<FileEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [sortField, setSortField] = useState<SortField>("serverModified")
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc")
  const [favoritePaths, setFavoritePaths] = useState<Set<string>>(new Set())

  function compareEntries(a: FileEntry, b: FileEntry): number {
    let cmp: number
    if (sortField === "name") {
      cmp = a.name.localeCompare(b.name)
    } else {
      const aVal = a[sortField] ? new Date(a[sortField]!).getTime() : 0
      const bVal = b[sortField] ? new Date(b[sortField]!).getTime() : 0
      cmp = aVal - bVal
    }
    return sortDirection === "asc" ? cmp : -cmp
  }

  function handleSortClick(field: SortField) {
    if (field === sortField) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortField(field)
    }
  }

  const sortedFiles = [...recentFiles].sort(compareEntries)

  useEffect(() => {
    loadRecentFiles()
  }, [])

  async function checkFavorites(files: FileEntry[]) {
    const paths = files.map((e) => e.path)
    if (paths.length === 0) return
    try {
      const res = await fetch("/api/favorites/check", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
        body: JSON.stringify({ paths }),
      })
      const data = await res.json()
      setFavoritePaths(new Set(data.favorited || []))
    } catch {
      // ignore
    }
  }

  async function handleToggleFavorite(path: string) {
    const isFav = favoritePaths.has(path)
    const entry = recentFiles.find((e) => e.path === path)
    try {
      if (isFav) {
        await fetch("/api/favorites", {
          method: "DELETE",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({ path }),
        })
        setFavoritePaths((prev) => { const next = new Set(prev); next.delete(path); return next })
      } else {
        await fetch("/api/favorites", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({ path, name: entry?.name || "", item_type: "file" }),
        })
        setFavoritePaths((prev) => new Set(prev).add(path))
      }
    } catch {
      // ignore
    }
  }

  async function loadRecentFiles() {
    setLoading(true)
    try {
      const res = await fetch("/api/recent_files", {
        headers: { "X-CSRF-Token": getCSRFToken() },
      })
      const data = await res.json()
      const files = data.files || []
      setRecentFiles(files)
      checkFavorites(files)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  function handleOpenFile(path: string) {
    const editorPath = path.replace(/^\/+/, "")
    const encodedPath = editorPath.split("/").map(segment => encodeURIComponent(segment)).join("/")
    router.visit(`/files/${encodedPath}`)
  }

  return (
    <>
      <Head title="Recent Files" />
      <div className="mx-auto max-w-3xl lg:max-w-5xl p-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 mb-4">
          <div />

          <div className="flex items-center gap-1.5 shrink-0">
            {/* Recent / Files toggle */}
            <div className="flex items-center rounded-md bg-muted p-0.5">
              <button
                className="px-2.5 py-1 text-xs font-medium rounded bg-white text-foreground dark:bg-background transition-colors"
              >
                Recent
              </button>
              <button
                onClick={() => router.visit("/files")}
                className="px-2.5 py-1 text-xs font-medium rounded text-muted-foreground hover:text-foreground transition-colors"
              >
                Files
              </button>
            </div>

            {/* Sort dropdown (icon only) */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer">
                <ArrowUpDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {(Object.keys(SORT_LABELS) as SortField[]).map((field) => (
                  <DropdownMenuItem key={field} onClick={() => handleSortClick(field)}>
                    <span className={sortField === field ? "font-medium" : ""}>{SORT_LABELS[field]}</span>
                    {sortField === field && (
                      sortDirection === "asc"
                        ? <ArrowUp className="h-3 w-3 ml-auto" />
                        : <ArrowDown className="h-3 w-3 ml-auto" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* New file button */}
            <button
              onClick={() => router.visit("/new")}
              className="flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              title="New file"
            >
              <Plus className="h-3.5 w-3.5" />
              New
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : sortedFiles.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No recent files yet. Start by capturing some content!
          </div>
        ) : (
          <div className="rounded-lg border bg-card shadow-xs overflow-hidden divide-y">
            {sortedFiles.map((entry) => (
              <div
                key={entry.path}
                className="group flex items-center gap-3 px-4 py-3 hover:bg-accent/50 cursor-pointer"
                onClick={() => handleOpenFile(entry.path)}
              >
                <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium">{entry.name}</p>
                </div>
                {entry.tags && Object.entries(entry.tags).map(([key, value]) => (
                  <Badge key={key} variant="secondary" className="text-xs">
                    {value}
                  </Badge>
                ))}
                <button
                  onClick={(e) => { e.stopPropagation(); handleToggleFavorite(entry.path) }}
                  className="opacity-0 group-hover:opacity-100 rounded-md p-1 hover:bg-accent transition-opacity"
                  title={favoritePaths.has(entry.path) ? "Remove from Favorites" : "Add to Favorites"}
                >
                  <Star className={`h-4 w-4 ${favoritePaths.has(entry.path) ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
